import { createSlice } from "@reduxjs/toolkit";
import {
  actionCreateEntryFiu,
  actionFetchAccountBalancesStatus,
  actionFetchAccountControlLogs,
  actionFetchAssetInrPrices,
  actionLockUnlockAccountBalance,
  actionUpdateAccountControlStatus,
  actionRequestLockUnlockAccountBalance,
  actionRequestUpdateAccountControlStatus,
  actionFetchAccountControlRequests,
  actionReviewAccountControlRequest,
} from "../action/fiuAction";
import { normalizeBalancesResponse } from "../../layout/FiuLayout/components/BlockBalance/blockBalanceUtils";

const initialAccountControls = {
  balancesStatus: null,
  portfolio: null,
  walletType: "trading",
  searchAccountId: "",
  balancesLoading: false,
  balancesError: null,
  logs: {
    count: 0,
    next: null,
    previous: null,
    results: [],
  },
  logsLoading: false,
  logsFilters: {
    email: "",
    assetSymbol: "",
    actionType: "",
    startDate: "",
    endDate: "",
    page: 0,
    pageSize: 10,
  },
  lockUnlockLoading: false,
  updateStatusLoading: false,
  assetPrices: {},
  assetPricesLoading: false,
  assetPricesUpdatedAt: null,
  requests: {
    count: 0,
    next: null,
    previous: null,
    results: [],
  },
  requestsLoading: false,
  requestsFilters: {
    email: "",
    accountId: "",
    assetSymbol: "",
    actionType: "",
    requestedBy: "",
    status: "",
    page: 0,
    pageSize: 10,
  },
  reviewLoading: false,
};

const initialState = {
  value: 0,
  selectedEmail: "",
  currentStep: 0,
  formStep: "c_o_i",
  fiuEntry: {
    ref_no: "",
    mobile_numbers: [],
    aadhaar: [],
    pan: [],
    email: [],
    bank_accounts: [],
    address: [],
    passport: [],
    voter_id: [],
    driving_license: [],
    cin: [],
    gstin: [],
    tan: [],
    wallet_addresses: [],
    sender_wallet_addresses: [],
    receiver_wallet_addresses: [],
    transaction_hashes: [],
    regulatory_body: "FIU",
  },
  fiuPlatformFilter: false,
  fiuEntryResponse: null,
  fiuEntryResponsePending: false,
  fiuEntryResponseError: false,
  editFiuEntry: null,
  accountControls: initialAccountControls,
};

export const fiuSlice = createSlice({
  name: "fiu",
  initialState,
  reducers: {
    saveFiuEntryBasicDetails: (state, { payload }) => {
      state.fiuEntry = { ...state.fiuEntry, ...payload };
    },
    saveFiuEntryData: (state, { payload }) => {
      state.fiuEntry = { ...state.fiuEntry, ...payload };
    },
    saveResetFiuData: (state, { payload }) => {
      state.fiuEntry = payload;
    },
    saveFiuEditData: (state, { payload }) => {
      state.editFiuEntry = payload;
    },
    saveFiuPlatformFilter: (state, { payload }) => {
      state.fiuPlatformFilter = payload;
    },
    setAccountControlsWalletType: (state, { payload }) => {
      state.accountControls.walletType = payload;
      if (state.accountControls.balancesStatus) {
        state.accountControls.portfolio = normalizeBalancesResponse(
          state.accountControls.balancesStatus,
          payload
        );
      }
    },
    setAccountControlsLogsFilters: (state, { payload }) => {
      state.accountControls.logsFilters = {
        ...state.accountControls.logsFilters,
        ...payload,
      };
    },
    clearAccountControlsBalances: (state) => {
      state.accountControls.balancesStatus = null;
      state.accountControls.portfolio = null;
      state.accountControls.balancesError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(actionCreateEntryFiu.pending, (state) => {
        state.fiuEntryResponsePending = true;
      })
      .addCase(actionCreateEntryFiu.fulfilled, (state, { payload }) => {
        state.fiuEntryResponse = payload.data;
        state.fiuEntryResponsePending = false;
      })
      .addCase(actionCreateEntryFiu.rejected, (state) => {
        state.fiuEntryResponsePending = false;
        state.fiuEntryResponseError = true;
      })

      .addCase(actionFetchAccountBalancesStatus.pending, (state) => {
        state.accountControls.balancesLoading = true;
        state.accountControls.balancesError = null;
      })
      .addCase(actionFetchAccountBalancesStatus.fulfilled, (state, { payload }) => {
        state.accountControls.balancesLoading = false;
        state.accountControls.balancesStatus = payload.data;
        state.accountControls.searchAccountId = payload.accountId;
        state.accountControls.walletType = payload.walletType;
        state.accountControls.portfolio = normalizeBalancesResponse(
          payload.data,
          payload.walletType
        );
      })
      .addCase(actionFetchAccountBalancesStatus.rejected, (state, { payload }) => {
        state.accountControls.balancesLoading = false;
        state.accountControls.balancesError = payload;
        state.accountControls.balancesStatus = null;
        state.accountControls.portfolio = null;
      })

      .addCase(actionFetchAccountControlLogs.pending, (state) => {
        state.accountControls.logsLoading = true;
      })
      .addCase(actionFetchAccountControlLogs.fulfilled, (state, { payload }) => {
        state.accountControls.logsLoading = false;
        state.accountControls.logs = payload.data;
        state.accountControls.logsFilters = payload.filters;
      })
      .addCase(actionFetchAccountControlLogs.rejected, (state) => {
        state.accountControls.logsLoading = false;
      })

      .addCase(actionFetchAssetInrPrices.pending, (state) => {
        state.accountControls.assetPricesLoading = true;
      })
      .addCase(actionFetchAssetInrPrices.fulfilled, (state, { payload }) => {
        state.accountControls.assetPricesLoading = false;
        state.accountControls.assetPrices = {
          ...state.accountControls.assetPrices,
          ...payload,
        };
        state.accountControls.assetPricesUpdatedAt = new Date().toISOString();
      })
      .addCase(actionFetchAssetInrPrices.rejected, (state) => {
        state.accountControls.assetPricesLoading = false;
      })

      .addCase(actionRequestLockUnlockAccountBalance.pending, (state) => {
        state.accountControls.lockUnlockLoading = true;
      })
      .addCase(actionRequestLockUnlockAccountBalance.fulfilled, (state) => {
        state.accountControls.lockUnlockLoading = false;
      })
      .addCase(actionRequestLockUnlockAccountBalance.rejected, (state) => {
        state.accountControls.lockUnlockLoading = false;
      })

      .addCase(actionRequestUpdateAccountControlStatus.pending, (state) => {
        state.accountControls.updateStatusLoading = true;
      })
      .addCase(actionRequestUpdateAccountControlStatus.fulfilled, (state) => {
        state.accountControls.updateStatusLoading = false;
      })
      .addCase(actionRequestUpdateAccountControlStatus.rejected, (state) => {
        state.accountControls.updateStatusLoading = false;
      })
      .addCase(actionFetchAccountControlRequests.pending, (state) => {
        state.accountControls.requestsLoading = true;
      })
      .addCase(
        actionFetchAccountControlRequests.fulfilled,
        (state, { payload }) => {
          state.accountControls.requestsLoading = false;
          state.accountControls.requests = payload.data;
          state.accountControls.requestsFilters = payload.filters;
        }
      )
      .addCase(actionFetchAccountControlRequests.rejected, (state) => {
        state.accountControls.requestsLoading = false;
      })
      .addCase(actionReviewAccountControlRequest.pending, (state) => {
        state.accountControls.reviewLoading = true;
      })
      .addCase(actionReviewAccountControlRequest.fulfilled, (state) => {
        state.accountControls.reviewLoading = false;
      })
      .addCase(actionReviewAccountControlRequest.rejected, (state) => {
        state.accountControls.reviewLoading = false;
      });
  },
});

export const {
  saveFiuEntryBasicDetails,
  saveFiuEntryData,
  saveFiuEditData,
  saveResetFiuData,
  saveFiuPlatformFilter,
  setAccountControlsWalletType,
  setAccountControlsLogsFilters,
  clearAccountControlsBalances,
} = fiuSlice.actions;

export default fiuSlice.reducer;
